import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useState } from 'react';
import { useSEO } from '../../utils/seo';

const JoinPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.masdeuna.org';
  
  useSEO({
    title: 'Join the Movement - Become a Mentor or Mentee | MasDeUna',
    description: 'Join MasDeUna as a mentor, mentee, or partner. Connect with Latina leaders, access professional development workshops, and support the movement elevating Latina leadership. Get involved today.',
    keywords: 'join MasDeUna, become a mentor, become a mentee, Latina mentorship, partnership opportunities, volunteer',
    canonical: `${siteUrl}/join`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Join the Movement',
      url: `${siteUrl}/join`,
      description: 'Join MasDeUna as a mentor, mentee, or partner to elevate Latina leadership',
      mainEntity: {
        '@type': 'Organization',
        name: 'MasDeUna',
        url: siteUrl
      }
    }
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    interest: '',
    course: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d52upiqbfdatfusc424g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', organization: '', interest: '', course: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const ways = [
    {
      title: 'Become a Mentee',
      description: 'Rising Latina talent ready to level up? Connect with leaders who get it.',
      icon: 'ri-user-star-line',
      color: 'primary',
    },
    {
      title: 'Become a Mentor',
      description: 'Latina leader with wisdom to share? Give back and shape the next generation.',
      icon: 'ri-hand-heart-line',
      color: 'secondary',
    },
    {
      title: 'Partner With Us',
      description: 'Organizations committed to Latina leadership? Let\'s build together.',
      icon: 'ri-team-line',
      color: 'electric',
    },
    {
      title: 'Workshop Information',
      description: 'Learn about our professional development workshops designed for Latina leaders.',
      icon: 'ri-book-open-line',
      color: 'primary',
    },
    {
      title: 'Teach a Workshop',
      description: 'Share your expertise and help shape the next generation of Latina leaders.',
      icon: 'ri-presentation-line',
      color: 'secondary',
    },
    {
      title: 'Support the Movement',
      description: 'Every dollar fuels mentorship, programs, and economic mobility for Latinas.',
      icon: 'ri-heart-3-line',
      color: 'electric',
    },
  ];

  const colorClasses = {
    primary: {
      bgLight: 'bg-primary/20',
      text: 'text-primary',
    },
    secondary: {
      bgLight: 'bg-secondary/20',
      text: 'text-secondary',
    },
    electric: {
      bgLight: 'bg-electric/20',
      text: 'text-electric',
    },
  };

  return (
    <div className="min-h-screen bg-warmWhite">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-primary/10 via-warmWhite to-secondary/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <h1 className="font-poppins font-black text-5xl sm:text-6xl md:text-7xl text-charcoal mb-6 leading-tight">
              Join the Movement
            </h1>
            <div className="w-24 h-2 bg-primary mx-auto mb-8"></div>
            <p className="font-inter text-xl text-charcoal/80 max-w-3xl mx-auto font-medium">
              This movement needs you. Whether you're looking to grow, give back, partner, or support—there's a place for you here.
            </p>
          </div>
        </div>
      </section>

      {/* Ways to Get Involved */}
      <section className="py-20 bg-warmWhite">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-poppins font-black text-4xl sm:text-5xl text-charcoal mb-4">
              Ways to Get Involved
            </h2>
            <p className="font-inter text-lg text-charcoal/70 max-w-2xl mx-auto">
              Choose your path and let's build together
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
            {ways.map((way, index) => {
              const colors = colorClasses[way.color as keyof typeof colorClasses];
              return (
                <div
                  key={index}
                  className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 group"
                >
                  <div className={`w-16 h-16 flex items-center justify-center ${colors.bgLight} rounded-xl mb-6 group-hover:scale-110 transition-transform`}>
                    <i className={`${way.icon} text-3xl ${colors.text}`}></i>
                  </div>
                  <h3 className="font-poppins font-bold text-2xl text-charcoal mb-3">{way.title}</h3>
                  <p className="font-inter text-base text-charcoal/70 leading-relaxed">{way.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-gradient-to-br from-charcoal via-charcoal to-primary/20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-poppins font-black text-4xl sm:text-5xl text-white mb-6">
              Let's Connect
            </h2>
            <div className="w-24 h-2 bg-primary mx-auto mb-8"></div>
            <p className="font-inter text-xl text-white/80 font-medium">
              Ready to take the next step? Fill out the form below and we'll be in touch.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white p-10 rounded-3xl shadow-2xl" data-readdy-form>
            <div className="space-y-6">
              <div>
                <label htmlFor="name" className="block font-inter font-semibold text-charcoal mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-charcoal/20 rounded-lg focus:border-primary focus:outline-none transition-colors font-inter text-sm"
                />
              </div>

              <div>
                <label htmlFor="email" className="block font-inter font-semibold text-charcoal mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-charcoal/20 rounded-lg focus:border-primary focus:outline-none transition-colors font-inter text-sm"
                />
              </div>

              <div>
                <label htmlFor="organization" className="block font-inter font-semibold text-charcoal mb-2">
                  Organization
                </label>
                <input
                  type="text"
                  id="organization"
                  name="organization"
                  value={formData.organization}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-charcoal/20 rounded-lg focus:border-primary focus:outline-none transition-colors font-inter text-sm"
                />
              </div>

              <div>
                <label htmlFor="interest" className="block font-inter font-semibold text-charcoal mb-2">
                  I'm interested in *
                </label>
                <select
                  id="interest"
                  name="interest"
                  value={formData.interest}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-charcoal/20 rounded-lg focus:border-primary focus:outline-none transition-colors font-inter text-sm cursor-pointer"
                >
                  <option value="">Select an option</option>
                  <option value="Becoming a Mentee">Becoming a Mentee</option>
                  <option value="Becoming a Mentor">Becoming a Mentor</option>
                  <option value="Partnership Opportunities">Partnership Opportunities</option>
                  <option value="Workshop Information">Workshop Information</option>
                  <option value="Teaching a Workshop">Teaching a Workshop</option>
                  <option value="Donating/Supporting">Donating/Supporting</option>
                  <option value="General Inquiry">General Inquiry</option>
                </select>
              </div>

              <div>
                <label htmlFor="course" className="block font-inter font-semibold text-charcoal mb-2">
                  Which workshop are you interested in?
                </label>
                <select
                  id="course"
                  name="course"
                  value={formData.course}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-charcoal/20 rounded-lg focus:border-primary focus:outline-none transition-colors font-inter text-sm cursor-pointer"
                >
                  <option value="">Select a workshop</option>
                  <option value="Leadership Fundamentals">Leadership Fundamentals</option>
                  <option value="Executive Presence">Executive Presence</option>
                  <option value="Negotiation Mastery">Negotiation Mastery</option>
                  <option value="Strategic Networking">Strategic Networking</option>
                  <option value="Financial Literacy">Financial Literacy</option>
                  <option value="Giving and Receiving Feedback">Giving and Receiving Feedback</option>
                  <option value="Strategic Delegation">Strategic Delegation</option>
                  <option value="Personal Branding">Personal Branding</option>
                  <option value="Time Management">Time Management</option>
                  <option value="General Inquiry">General Inquiry</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block font-inter font-semibold text-charcoal mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 border-2 border-charcoal/20 rounded-lg focus:border-primary focus:outline-none transition-colors font-inter text-sm resize-none"
                ></textarea>
                <div className="text-right text-sm text-charcoal/60 mt-1 font-inter">
                  {formData.message.length}/500
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary hover:bg-primary/90 text-white font-inter font-bold text-lg px-8 py-4 rounded-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-secondary/10 border border-secondary text-charcoal px-6 py-4 rounded-lg font-inter">
                  Message sent successfully! We'll be in touch soon.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-primary/10 border border-primary text-charcoal px-6 py-4 rounded-lg font-inter">
                  Something went wrong. Please try again.
                </div>
              )}
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default JoinPage;
